import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType, StatusCode } from "api/protocols";
import { CancelToken } from "axios";
import { TCustomerNote } from "pages/secure/facility/customer/context/contextTypes";
import { ICustomer } from "redux/reducers/models/customer";
import { IKind } from "redux/reducers/models/transaction";
import { ICustomerPaymentMethod } from "../../customer/paymentMethod";

interface IGetCustomer {
  id?: number;
  search?: string;
  state?: string;
  "address-lines"?: boolean;
  "default-address-line"?: boolean;
  memberships?: boolean;
  preferences?: boolean;
  limit?: number;
  offset?: number;
  customer_type?: number[];
  cart_customer_type?: number[];
}

interface IGetCustomerAll {
  email: string;
  phone: string;
  full_name: string;
}

interface IPostCustomer {
  id?: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  member_number?: number;
  send_welcome_email?: boolean;
  customer_type_id?: number;
  marketing_email?: boolean;
  state?: string;
}

interface ICustomerRes {
  status: StatusCode;
  message?: string;
  data: any;
}

export interface IGetCustomerPaymentMethodParams {
  id?: number;
  customer_id?: number;
}

export interface IPostPaymentMethodParams {
  payment_method_id: string;
  customer_id: number;
  save_card: boolean;
  save_on_profile?: boolean;
}

interface IExportCustomerListRes {
  status: StatusCode;
  data: string;
}

// GET Cart
// Retrieve a list of customers
export async function GetCustomer(params: IGetCustomer, useGlobalLoader?: boolean, cancelToken?: CancelToken) {
  return await APIGet<Array<ICustomer>>(
    APIUrl(AdminType.FacilityAdmin, "/customer"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function GetCustomerAll(params: IGetCustomerAll, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/customer/all"), params, useGlobalLoader);
}

export async function PostCustomer(params: IPostCustomer, useGlobalLoader: boolean): Promise<ICustomerRes> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer"), params, useGlobalLoader);
}

export async function PostExistingCustomer(
  params: { customer_id: number },
  useGlobalLoader: boolean,
): Promise<ICustomerRes> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/existing"), params, useGlobalLoader);
}

export async function PutCustomer(params: any, useGlobalLoader: boolean): Promise<ICustomerRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer"), params, useGlobalLoader);
}

export async function PutBanCustomer(
  params: { id: number; banned: boolean; banned_reason?: string },
  useGlobalLoader: boolean,
): Promise<ICustomerRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/ban"), params, useGlobalLoader);
}

export async function DeleteCustomer(params: { id: number }, useGlobalLoader: boolean): Promise<ICustomerRes> {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/customer"), params, useGlobalLoader);
}

// Export Customer List
export async function ExportCustomerList(params: any, useGlobalLoader: boolean): Promise<IExportCustomerListRes> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/export"), params, useGlobalLoader);
}

// Upload Customer Profile Image
export async function UploadCustomerProfileImage(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/profile-image"), params, useGlobalLoader);
}

// Delete Customer Profile Image
export async function DeleteCustomerProfileImage(params: any, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/customer/profile-image"), params, useGlobalLoader);
}

//Get Customer Notes
export async function GetCustomerNotes(params: any, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<TCustomerNote[]>(
    APIUrl(AdminType.FacilityAdmin, "/customer/note"),
    params,
    useGlobalLoader,
    token,
  );
}

//POST Customer Notes
export async function PostCustomerNote(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/note"), params, useGlobalLoader);
}

//Delete Customer Note
export async function DeleteCustomerNote(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/customer/note"), params, useGlobalLoader);
}

export async function SendPasswordReset(params: { email: string }, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/reset"), params, useGlobalLoader);
}

export async function GetCustomerPaymentMethod(
  params: IGetCustomerPaymentMethodParams,
  useGlobalLoader?: boolean,
  token?: CancelToken,
) {
  return await APIGet<ICustomerPaymentMethod[]>(
    APIUrl(AdminType.FacilityAdmin, "/customer/payment-method"),
    params,
    useGlobalLoader,
    token,
  );
}

interface IDownloadCustomerAccountStatements {
  account_statement_id: number;
  account_id: number;
  start_date: Date;
  end_date: Date;
}

export async function DownloadCustomerAccountStatements(
  params: Partial<IDownloadCustomerAccountStatements>,
  useGlobalLoader: boolean,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/client/account/statement/download"), params, useGlobalLoader);
}

export async function EmailCustomerAccountStatement(
  params: { id: number; account_id: number; email?: string },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account/statement/email"), params, useGlobalLoader);
}

export async function GetCustomerHouseAccounts(
  params: { customer_id: number; closed?: boolean },
  useGlobalLoader?: boolean,
  token?: CancelToken,
): Promise<ICustomerRes> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/client/account"), params, useGlobalLoader, token);
}

export async function GetAccountTransactionReceipt(
  params: { account_transaction_id: number },
  useGlobalLoader: boolean,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/client/account/transaction/receipt"), params, useGlobalLoader);
}

export async function GetCustomerStatements(
  params: { id?: number; account_id?: number },
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/client/account/statement"), params, useGlobalLoader, token);
}

export async function PutMergeCustomers(
  params: {
    parent_id: number;
    child_id: number;
    bookings: boolean;
    memberships: boolean;
    tickets: boolean;
    house_accounts: boolean;
    gift_cards: boolean;
    orders: boolean;
    credit_books: boolean;
    loyalty_points: boolean;
    notes: boolean;
    playing_partners: boolean;
    reservations: boolean;
  },
  useGlobalLoader: boolean,
): Promise<ICustomerRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/merge"), params, useGlobalLoader);
}

export async function SendWelcomeEmail(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/welcome"), params, useGlobalLoader);
}

export async function SendBirthdayRequestEmail(params: { email: string }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/birthday-request"), params, useGlobalLoader);
}

export async function SendBirthdayRequestEmails(
  params: { booking_category_ids?: number[] },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/birthday-requests"), params, useGlobalLoader);
}

export interface IPutCustomerPreferences {
  customer_id: number;
  marketing_email: boolean;
  booking_confirmation_email: boolean;
  booking_reminder_email: boolean;
  booking_update_email: boolean;
  booking_cancellation_email: boolean;
  display_name_on_tee_sheet?: boolean;
}

export async function PutCustomerPreferences(params: IPutCustomerPreferences, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/preference"), params, useGlobalLoader);
}

//Customer Tags
export interface ICustomerTag {
  customer_id?: number;
  tag_id?: number;
}

export async function GetCustomerTag(params: ICustomerTag, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/customer/tag"), params, useGlobalLoader, token);
}
export async function PutCustomerTag(params: ICustomerTag, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/tag"), params, useGlobalLoader);
}

export async function DeleteCustomerTag(params: ICustomerTag, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/customer/tag"), params, useGlobalLoader);
}

export async function PostPaymentMethod(params: IPostPaymentMethodParams, useGlobalLoader?: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/payment-method"), params, useGlobalLoader);
}

export async function PostPaymentMethodRequest(params: { customer_id: number }, useGlobalLoader?: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/payment-method/request"), params, useGlobalLoader);
}

export async function PostSetup(params: { customer_id?: number }, useGlobalLoader?: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/payment-method/setup"), params, useGlobalLoader);
}
interface IPostCustomerAccountTransaction {
  kind: IKind;
  amount: number;
  account_id: number;
  payment_option_id: number;
  gift_card_code?: string;
  gift_card_pin?: string;
  note?: string;
  credit_book_id?: number;
  register_id?: number;
}

export interface IAccountTransaction {
  account_id: number;
  amount: number;
  authorization_id: number;
  client_id: number;
  client_secret: string;
  currency: string;
  device: string;
  error_code: string;
  facility_id: number;
  gateway: string;
  id: number;
  kind: string;
  note: string;
  order_transaction_id: number;
  parent_id: number;
  payment_intent_id: string;
  payment_last_4: string;
  payment_method: string;
  payment_type: string;
  processed_at: string;
  processing_type: string;
  source: string;
}
export interface IPostAccountTransactionResponse {
  data: IAccountTransaction;
  status: StatusCode;
}

interface ICustomerAccountTransactionCapture {
  transaction_id: number;
  payment_intent_id: string;
}

interface IDeleteCustomerAddressLine {
  customer_id: number;
  address_line_id: number;
}

interface IPostCustomerAddressLine {
  customer_id: number;
  first_name?: string;
  last_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  postal?: string;
  phone?: string;
  province_id: number;
  country_id: number;
  default: boolean;
}

interface IPutCustomerAddressLine extends IPostCustomerAddressLine {
  id: number;
}

export async function PostAccountTransaction(
  params: IPostCustomerAccountTransaction,
  useGlobalLoader: boolean,
): Promise<IPostAccountTransactionResponse> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/client/account/transaction"), params, useGlobalLoader);
}

export async function PutAccountTransactionCapture(
  params: ICustomerAccountTransactionCapture,
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account/transaction/capture"), params, useGlobalLoader);
}

export async function PutCustomerNote(
  params: { id: number; content: string; high_priority?: boolean },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/note"), params, useGlobalLoader);
}

export async function DeleteCustomerAddressLine(params: IDeleteCustomerAddressLine, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/customer/address-line"), params, useGlobalLoader);
}

export async function PostCustomerAddressLine(params: IPostCustomerAddressLine, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/address-line"), params, useGlobalLoader);
}

export async function PutCustomerAddressLine(params: IPutCustomerAddressLine, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/customer/address-line"), params, useGlobalLoader);
}

export interface IEmergencyContact {
  id: number;
  first_name: string;
  last_name: string;
  relation: string;
  phone: string;
  email: string;
  profile_image_source: string;
}

interface IGetCustomerEmergencyContactsParams {
  emergency_contact_ids: Array<number>;
  customer_id: number;
}

export async function GetCustomerEmergencyContacts(
  params: IGetCustomerEmergencyContactsParams,
  useGlobalLoader: boolean,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/customer/emergency"), params, useGlobalLoader);
}

interface IPutCustomerResponse {
  data: IEmergencyContact[];
  status: number;
}

export async function PutCustomerEmergencyContacts(
  params: { customer_id: number; contact: IEmergencyContact },
  useGlobalLoader: boolean,
) {
  return await APIPut<IPutCustomerResponse>(
    APIUrl(AdminType.FacilityAdmin, "/customer/emergency"),
    params,
    useGlobalLoader,
  );
}

interface IDeleteCustomerEmergencyContactParms {
  emergency_contact_id: number;
  user_id: number;
}

export async function DeleteCustomerEmergencyContact(
  params: IDeleteCustomerEmergencyContactParms,
  useGlobalLoader: boolean,
) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/customer/emergency"), params, useGlobalLoader);
}

interface IPostCustomerEmergencyContactsParams {
  customer_id: number;
  contact: IEmergencyContact;
}

export async function PostUserEmergencyContact(params: IPostCustomerEmergencyContactsParams, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/emergency"), params, useGlobalLoader);
}

export async function GetCustomerAuditTrail(params: { customer_id: number }, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/customer/audit"), params, useGlobalLoader);
}

export async function PostCustomerImport(params: FormData, useGlobalLoader: boolean): Promise<ICustomerRes> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/customer/import"), params, useGlobalLoader);
}
